import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Users,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  Eye,
  BarChart3,
  TrendingUp,
  UserPlus,
  Crown,
  Shield,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  FileText,
} from "lucide-react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Modal states
  const [deleteUser, setDeleteUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [userComparisons, setUserComparisons] = useState([]);

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        await axios.get(`${API}/admin/check`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        setIsAdmin(true);
      } catch (error) {
        setIsAdmin(false);
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    };
    checkAdmin();
  }, [navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/admin/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit,
      });
      if (search) params.append("search", search);
      if (tierFilter !== "all") params.append("tier", tierFilter);

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search, tierFilter]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchStats();
    }
  }, [isAdmin, fetchUsers, fetchStats]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, tierFilter]);

  // Update user tier
  const handleUpdateTier = async (userId, newTier) => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { tier: newTier },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      toast.success(`User tier updated to ${newTier}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/admin/users/${deleteUser.user_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      toast.success("User deleted successfully");
      setDeleteUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Reset user usage
  const handleResetUsage = async (userId) => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/admin/users/${userId}/reset-usage`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      toast.success("Usage counter reset");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to reset usage");
    }
  };

  // View user details
  const handleViewUser = async (userId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setViewUser(response.data.user);
      setUserComparisons(response.data.comparisons);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  // Save edited user
  const handleSaveEdit = async () => {
    if (!editUser) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.patch(
        `${API}/admin/users/${editUser.user_id}`,
        {
          name: editUser.name,
          tier: editUser.tier,
          comparisons_used: editUser.comparisons_used,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      toast.success("User updated successfully");
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case "pro":
        return "bg-secondary-main/20 text-secondary-main border-secondary-main/30";
      case "standard":
        return "bg-primary-main/20 text-primary-main border-primary-main/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case "pro":
        return <Crown className="w-3 h-3" />;
      case "standard":
        return <Shield className="w-3 h-3" />;
      default:
        return null;
    }
  };

  if (isAdmin === null) {
    return (
      <div className="min-h-screen bg-[#020408] flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full border-2 border-primary-main" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#020408]">
      <Navbar user={user} />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
                <span className="text-secondary-main">Admin</span> Dashboard
              </h1>
              <p className="text-gray-400">
                Manage users, subscriptions, and platform settings
              </p>
            </div>

            <Button
              onClick={() => {
                fetchUsers();
                fetchStats();
              }}
              variant="outline"
              className="border-primary-main/50 text-primary-main hover:bg-primary-main/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary-main" />
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    Total Users
                  </span>
                </div>
                <span className="font-display text-2xl font-bold">
                  {stats.total_users}
                </span>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-secondary-main" />
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    Pro
                  </span>
                </div>
                <span className="font-display text-2xl font-bold text-secondary-main">
                  {stats.users_by_tier.pro}
                </span>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-primary-main" />
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    Standard
                  </span>
                </div>
                <span className="font-display text-2xl font-bold text-primary-main">
                  {stats.users_by_tier.standard}
                </span>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="w-4 h-4 text-accent-success" />
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    New (7d)
                  </span>
                </div>
                <span className="font-display text-2xl font-bold text-accent-success">
                  {stats.new_users_week}
                </span>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-accent-warning" />
                  <span className="text-gray-400 text-xs uppercase tracking-wider">
                    Comparisons
                  </span>
                </div>
                <span className="font-display text-2xl font-bold text-accent-warning">
                  {stats.total_comparisons}
                </span>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass rounded-xl p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-background-subtle border-border"
                  data-testid="search-input"
                />
              </div>

              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger
                  className="w-full md:w-[180px] bg-background-subtle border-border"
                  data-testid="tier-filter"
                >
                  <SelectValue placeholder="Filter by tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Users Table */}
          <div className="glass rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-gray-400">User</TableHead>
                  <TableHead className="text-gray-400">Tier</TableHead>
                  <TableHead className="text-gray-400">Usage</TableHead>
                  <TableHead className="text-gray-400">Comparisons</TableHead>
                  <TableHead className="text-gray-400">Joined</TableHead>
                  <TableHead className="text-gray-400 text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="flex justify-center">
                        <div className="animate-pulse-glow w-8 h-8 rounded-full border-2 border-primary-main" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-12 text-gray-400"
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => (
                    <TableRow
                      key={u.user_id}
                      className="border-border hover:bg-background-subtle/50"
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img
                              src={u.picture}
                              alt={u.name}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary-main/20 flex items-center justify-center">
                              <span className="text-primary-main text-sm font-bold">
                                {u.name?.charAt(0)?.toUpperCase() || "?"}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white">{u.name}</p>
                            <p className="text-gray-400 text-sm">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={u.tier}
                          onValueChange={(value) =>
                            handleUpdateTier(u.user_id, value)
                          }
                        >
                          <SelectTrigger
                            className={`w-[120px] h-8 text-xs border ${getTierBadgeClass(
                              u.tier
                            )}`}
                            data-testid={`tier-select-${u.user_id}`}
                          >
                            <div className="flex items-center gap-1">
                              {getTierIcon(u.tier)}
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">
                            {u.comparisons_used}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-400 hover:text-primary-main"
                            onClick={() => handleResetUsage(u.user_id)}
                            title="Reset usage"
                            data-testid={`reset-usage-${u.user_id}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {u.comparisons_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-primary-main"
                            onClick={() => handleViewUser(u.user_id)}
                            data-testid={`view-user-${u.user_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-primary-main"
                            onClick={() => setEditUser({ ...u })}
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-400 hover:text-red-400"
                            onClick={() => setDeleteUser(u)}
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <span className="text-gray-400 text-sm">
                  Showing {page * limit + 1} -{" "}
                  {Math.min((page + 1) * limit, total)} of {total}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="border-border"
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={(page + 1) * limit >= total}
                    className="border-border"
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent className="bg-background-paper border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              Delete User?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete{" "}
              <span className="text-white font-medium">
                {deleteUser?.email}
              </span>{" "}
              and all their comparisons. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border hover:bg-background-subtle">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-background-paper border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Edit User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update user details and subscription tier
            </DialogDescription>
          </DialogHeader>

          {editUser && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email</label>
                <Input
                  value={editUser.email}
                  disabled
                  className="bg-background-subtle border-border text-gray-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Name</label>
                <Input
                  value={editUser.name}
                  onChange={(e) =>
                    setEditUser({ ...editUser, name: e.target.value })
                  }
                  className="bg-background-subtle border-border"
                  data-testid="edit-name"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Tier</label>
                <Select
                  value={editUser.tier}
                  onValueChange={(value) =>
                    setEditUser({ ...editUser, tier: value })
                  }
                >
                  <SelectTrigger
                    className="bg-background-subtle border-border"
                    data-testid="edit-tier"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">
                  Comparisons Used
                </label>
                <Input
                  type="number"
                  min="0"
                  value={editUser.comparisons_used}
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      comparisons_used: parseInt(e.target.value) || 0,
                    })
                  }
                  className="bg-background-subtle border-border"
                  data-testid="edit-usage"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditUser(null)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="bg-primary-main text-black hover:bg-primary-light"
              data-testid="save-edit"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={!!viewUser} onOpenChange={() => setViewUser(null)}>
        <DialogContent className="bg-background-paper border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">User Details</DialogTitle>
          </DialogHeader>

          {viewUser && (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <div className="flex items-center gap-4">
                {viewUser.picture ? (
                  <img
                    src={viewUser.picture}
                    alt={viewUser.name}
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary-main/20 flex items-center justify-center">
                    <span className="text-primary-main text-2xl font-bold">
                      {viewUser.name?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="font-display text-xl font-semibold">
                    {viewUser.name}
                  </h3>
                  <p className="text-gray-400">{viewUser.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`px-2 py-0.5 text-xs uppercase rounded border ${getTierBadgeClass(
                        viewUser.tier
                      )}`}
                    >
                      {viewUser.tier}
                    </span>
                    <span className="text-gray-500 text-sm">
                      Joined {new Date(viewUser.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="glass rounded-lg p-4">
                  <span className="text-gray-400 text-sm">Comparisons Used</span>
                  <p className="font-display text-2xl font-bold">
                    {viewUser.comparisons_used}
                  </p>
                </div>
                <div className="glass rounded-lg p-4">
                  <span className="text-gray-400 text-sm">
                    Total Comparisons
                  </span>
                  <p className="font-display text-2xl font-bold">
                    {userComparisons.length}
                  </p>
                </div>
              </div>

              {/* Comparisons List */}
              {userComparisons.length > 0 && (
                <div>
                  <h4 className="font-display text-lg font-semibold mb-3">
                    Comparisons
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userComparisons.map((comp) => (
                      <div
                        key={comp.comparison_id}
                        className="glass rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-primary-main" />
                          <div>
                            <p className="font-medium">{comp.name}</p>
                            <p className="text-gray-400 text-sm">
                              {comp.vendors_count} vendors •{" "}
                              {new Date(comp.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-0.5 text-xs uppercase rounded ${
                            comp.status === "completed"
                              ? "bg-accent-success/10 text-accent-success"
                              : comp.status === "compared"
                              ? "bg-primary-main/10 text-primary-main"
                              : "bg-gray-500/10 text-gray-400"
                          }`}
                        >
                          {comp.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
